import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import {Stack, Grid} from '@mui/material';
import {ChartsLabelMark, LineChart, PieChart} from '@mui/x-charts';
import {XYVO} from "@/service/analyze";
import {useMemo} from "react";
import {groupBy, map, uniq} from "lodash";
import {formatTime} from "@/helper/method";
import dayjs from "dayjs";
import {LineSeriesType} from "@mui/x-charts/models/seriesType/line";
import {lineElementClasses} from "@mui/x-charts/LineChart";

const colors = ["#00a1ff", "#00ceb6"]

export interface YearCustomerTrendProps {
    customers?: XYVO[]
    customerDist?: XYVO[]
    loading: boolean
}

export default function YearCustomerTrend({customers, customerDist, loading}: YearCustomerTrendProps) {
    const {xDatas, lSeries, pSeries, legends} = useMemo(() => {
        const groupYear = groupBy(customers, item => formatTime("Y", item.x))
        const xDatas = uniq(map(customers, item => `${dayjs(item.x).format("M")}月`))
        const lSeries: LineSeriesType[] = []
        const legends: { [key: string]: string }[] = []
        Object.keys(groupYear).map((key: string, index: number) => {
            const data = groupYear[key]?.map(item => item.y)
            const label = `${key}年`
            lSeries.push({
                data: data, label, valueFormatter: value => `${value}个`,
                showMark: false, type: 'line', color: colors[index],
            })
            legends.push({label: label, color: colors[index]})
        })

        const pSeries = customerDist?.map((item, index) => {
            const radius = [{
                innerRadius: 0,
                outerRadius: 80
            }, {
                innerRadius: 100,
                outerRadius: 120,
            }]
            const data = {
                ...radius[index],
                id: item.x,
                data: [
                    {
                        label: index == 0 ? legends[index].label : '意向客户',
                        value: item.y1,
                        color: "#edacbf",
                    },
                    {
                        label: index == 0 ? legends[index].label : '成交客户',
                        value: item.y2,
                        color: "#ffd68f",
                    },
                ],
            }
            return data
        })
        return {xDatas, lSeries, pSeries, legends}
    }, [customers, customerDist,]);
    return (
        <Card variant="outlined" sx={{width: '100%'}}>
            <CardContent>
                <Stack alignContent={'start'} alignItems={'start'} direction={'row'} justifyContent={'space-between'}>
                    <Typography component="h2" variant="subtitle2" gutterBottom>
                        近两年新增客户
                    </Typography>
                    <Stack alignItems={'start'} direction={'row'} spacing={2}>
                        {legends?.map((item, index: number) => <Stack key={index} alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={item.color}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>{item.label}</Typography>
                        </Stack>)}

                        {/*<Stack alignItems={'center'} spacing={1} direction={'row'}>*/}
                        {/*    <ChartsLabelMark type={'square'} color={'yellow'}/>*/}
                        {/*    <Typography sx={{display: 'inline-block', alignItems: 'start'}}>{`${2025}年`}</Typography>*/}
                        {/*</Stack>*/}
                    </Stack>
                </Stack>
                <Grid container={true} spacing={1}>
                    <Grid size={8}>
                        <LineChart
                            height={180}
                            series={lSeries}
                            hideLegend={true}
                            sx={{
                                [`& .${lineElementClasses.root}`]: {
                                    strokeWidth: 4,
                                },
                            }}
                            slotProps={{
                                legend: {
                                    direction: 'horizontal',
                                    position: {
                                        vertical: 'top',
                                        horizontal: 'end',
                                    },
                                    sx: {

                                        gap: '16px',
                                        // CSS class
                                        ['.MuiChartsLegend-series']: {
                                            gap: '8px',
                                        },
                                    },
                                },
                            }}
                            xAxis={[{
                                scaleType: 'band',
                                data: xDatas,
                                tickInterval: (index, i) => i % 2 === 0,
                                disableTicks: true,
                                disableLine: false,
                            }]}
                            yAxis={[{width: 28, disableTicks: true, disableLine: true}]}
                            margin={{left: 0, right: 0, top: 10, bottom: 0}}
                        />
                    </Grid>
                    <Grid size={4}>
                        <PieChart
                            margin={{left: 0, right: 0, top: 10, bottom: 0}}
                            series={pSeries || []}
                            width={300}
                            height={180}
                            hideLegend
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
